import { getApiUrl } from 'modules/enrollment-platform-api'
import axios, { AxiosError } from 'axios'
import {
  ApplicationEngineQueryParams,
  BenefitQueryResultDto,
  PaginatedBenefitsResponse,
  UpdateBenefitRequestDto,
  BulkUpdateBenefitRequestDto,
  BulkUpdateBenefitResponseDto,
  BatchUpdateBenefitRequestDto,
  BatchUpdateBenefitResponseDto,
  BenefitsQueryDto,
  BulkUpdateApplicationRequestDto,
} from './dtos'
import getApiToken from 'enrollment-platform/api/get-api-token'
import { serializeQueryParams } from 'hooks/useSearchParams'
import { RenewalResponseDto } from './dtos/Renewal.dto'
import { Benefit } from 'types/benefit'

export interface RenewalBulkUpdateResponseDto {
  successIds: string[]
  errors: string[]
}

/* -------------------------------------------------------------------------- */
/*                                   Renewal                                  */
/* -------------------------------------------------------------------------- */

/**
 * Renewal-specific bulk update — each benefit can carry its own data payload.
 * Different from bulkUpdateBenefits which applies the same data to all selected benefits.
 * @param benefits - Array of benefits with individual payloads
 * @returns Success and error IDs from the bulk update
 */
export const renewalBulkUpdate = async (
  benefits: Benefit[]
): Promise<RenewalBulkUpdateResponseDto> => {
  const idToken = getApiToken()
  const response = await axios.patch(
    `benefits/bulk-update`,
    { benefits },
    {
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
    }
  )
  return response.data
}

/**
 * Fetch renewal applications for a given client
 * @param clientPublicKey - The public key identifier for the client
 * @returns Renewal response data
 */
export const fetchRenewalApplications = async (
  clientPublicKey: string
): Promise<RenewalResponseDto> => {
  const idToken = getApiToken()
  const response = await axios.get(`benefits/renewal/${clientPublicKey}`, {
    baseURL: getApiUrl(),
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
  })
  return response.data
}

/* -------------------------------------------------------------------------- */
/*                             Application Engine                             */
/* -------------------------------------------------------------------------- */

/**
 * Fetch paginated application engine data from the v2 benefits API
 * This endpoint has pre-applied filters for non-Medicare, HealthBundle, active, non-test users
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated benefits response
 */
export const fetchApplication = async (
  params: ApplicationEngineQueryParams
): Promise<PaginatedBenefitsResponse> => {
  const idToken = getApiToken()

  const response = await axios.get(
    `api/v2/benefits/application-engine?${serializeQueryParams(params)}`,
    {
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
    }
  )

  return response.data
}

/**
 * Bulk update multiple applications with the same data payload
 * @param body - Contains the update data and benefit IDs to apply it to
 * @returns Bulk update response with results
 */
export const bulkUpdateApplication = async (
  body: BulkUpdateApplicationRequestDto
): Promise<BulkUpdateBenefitResponseDto> => {
  const idToken = getApiToken()
  const response = await axios.patch(
    `api/v2/benefits/application-engine`,
    body,
    {
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
    }
  )
  return response.data
}

export interface ExportBatch {
  data: BenefitQueryResultDto[]
  hasMore: boolean
  emitted: number
  total: number
  progressPct: number
  elapsedMs: number
  estimatedRemainingMs: number | null
}

/**
 * Streams benefit rows from the NDJSON export endpoint.
 * Each batch contains up to 500 rows. Call `onBatch` for every batch received.
 * Pass an `AbortSignal` to cancel mid-stream.
 *
 * Note: `page`, `limit`, `sort`, and `order` are not accepted by the endpoint.
 * Rows are flat (one per application) with `groupId`, `isPrimary`, `isSplitBundle`,
 * `matchesFilter`, and `matchesFilterReason`. `persons`, `enrollment`, and `client`
 * are populated by the server.
 */
export const streamApplicationExport = async (
  params: Omit<Partial<BenefitsQueryDto>, 'populate'>,
  onBatch: (batch: ExportBatch) => void,
  signal?: AbortSignal
): Promise<void> => {
  const idToken = getApiToken()
  const url = `${getApiUrl()}/api/v2/benefits/application-engine/export?${serializeQueryParams(params)}`
  const res = await fetch(url, {
    headers: { Authorization: idToken, Accept: 'application/x-ndjson' },
    signal,
  })

  // Early failure — server rejected before sending any data
  if (!res.ok || !res.body) {
    throw new Error(`Export failed: ${res.status}`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  // Wrapped in an object so TypeScript tracks the mutation through the closure
  const state = { lastBatch: null as ExportBatch | null }

  const processBatch = (line: string) => {
    const batch = JSON.parse(line) as ExportBatch
    state.lastBatch = batch
    onBatch(batch)
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed) continue
      processBatch(trimmed)
    }
  }
  if (buffer.trim()) {
    processBatch(buffer.trim())
  }

  // Mid-stream failure — stream closed early before the final batch
  if (state.lastBatch?.hasMore === true) {
    throw new Error(
      'Export stream closed unexpectedly before all data was received. Please try again later.'
    )
  }
}

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

/**
 * Fetch paginated list of benefits data from the v2 benefits API
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated benefits response
 */
export const fetchBenefits = async (
  params: BenefitsQueryDto
): Promise<PaginatedBenefitsResponse> => {
  const idToken = getApiToken()
  const response = await axios.get(
    `api/v2/benefits?${serializeQueryParams(params)}`,
    {
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
    }
  )
  return response.data
}

/**
 * Fetch list of benefit carriers, optionally filtered by active status and Medicare exclusion
 * @param isActive - Filter by active status
 * @param excludeMedicare - Exclude Medicare carriers
 * @returns Array of carrier names
 */
export const fetchBenefitCarriers = async (
  isActive?: boolean,
  excludeMedicare?: boolean
): Promise<string[]> => {
  const idToken = getApiToken()
  const response = await axios.get(`api/v2/benefits/carriers`, {
    baseURL: getApiUrl(),
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    params: { isActive, excludeMedicare },
  })

  return response.data
}

export interface UpdateAssigneeParams {
  assignee: string
  benefits: string[]
}

/**
 * Update the assignee for one or more benefits
 * @param body - Contains assignee ID and list of benefit IDs to update
 * @returns Axios response
 * @deprecated Use {@link updateBenefit}
 */
export const updateAssignee = async (body: UpdateAssigneeParams) => {
  const idToken = getApiToken()
  try {
    const response = await axios.put('/api/v1/benefits/assignee', body, {
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
    })
    return response
  } catch (err: unknown) {
    const axiosError = err as AxiosError
    const message =
      axiosError?.response?.data?.message ||
      'There was an error updating the assignee, please try again.'
    throw new Error(message)
  }
}

/**
 * Update a single benefit by ID — PATCH /api/v2/benefits/:benefitId.
 * Payment/ACH fields only; writes one pa.payment_info_update audit event.
 * @param benefitId - The ID of the benefit to update
 * @param data - The update payload
 * @returns Updated benefit query result
 */
export const updateBenefit = async (
  benefitId: string,
  data: UpdateBenefitRequestDto
): Promise<BenefitQueryResultDto> => {
  const idToken = getApiToken()
  const response = await axios.patch(`api/v2/benefits/${benefitId}`, data, {
    baseURL: getApiUrl(),
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
  })
  return response.data
}

/**
 * Bulk update multiple benefits with the same data payload
 * @param body - Contains the update data and benefit IDs to apply it to
 * @returns Bulk update response with results
 */
export const bulkUpdateBenefits = async (
  body: BulkUpdateBenefitRequestDto
): Promise<BulkUpdateBenefitResponseDto> => {
  const idToken = getApiToken()
  const response = await axios.patch(`api/v2/benefits`, body, {
    baseURL: getApiUrl(),
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
  })
  return response.data
}

/**
 * Fetch a single benefit by ID with all relations populated.
 * Used as a fallback in the CSV preview when a benefit ID is not in the current selection.
 * @param benefitId - The ID of the benefit to fetch
 * @returns The benefit query result with persons, enrollment, and client populated
 */
export const fetchBenefitById = async (
  benefitId: string
): Promise<BenefitQueryResultDto> => {
  const idToken = getApiToken()
  const response = await axios.get(
    `api/v2/benefits/${benefitId}?populate=persons,enrollment:slim,client`,
    {
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
    }
  )
  return response.data
}

/**
 * Batch benefit update — PATCH /api/v2/benefits/batch.
 * Different payload per benefit; applied individually.
 */
export const batchUpdateBenefits = async (
  body: BatchUpdateBenefitRequestDto
): Promise<BatchUpdateBenefitResponseDto> => {
  const idToken = getApiToken()
  const response = await axios.patch(`api/v2/benefits/batch`, body, {
    baseURL: getApiUrl(),
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
  })
  return response.data
}
